import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Plus, Edit, Trash2, Download } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { createBook, updateBook, deleteBook } from '../../utils/api';

export const BooksManager = ({ books, onUpdate, onExport }) => {
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    title: '', author: '', description: '', category: '', price: '', cover_image: '', pdf_filename: '', status: 'available'
  });

  const handleAdd = () => {
    setEditingItem(null);
    setFormData({ title: '', author: '', description: '', category: '', price: '', cover_image: '', pdf_filename: '', status: 'available' });
    setDialogOpen(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({ title: item.title || '', author: item.author || '', description: item.description || '', category: item.category || '', price: item.price || '', cover_image: item.cover_image || '', pdf_filename: item.pdf_filename || '', status: item.status || 'available' });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await updateBook(editingItem.id, formData);
        toast({ title: 'Success', description: 'Book updated' });
      } else {
        await createBook(formData);
        toast({ title: 'Success', description: 'Book created' });
      }
      setDialogOpen(false);
      onUpdate();
    } catch (error) {
      toast({ title: 'Error', description: 'Operation failed', variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this book?')) return;
    try {
      await deleteBook(id);
      toast({ title: 'Success', description: 'Book deleted' });
      onUpdate();
    } catch (error) {
      toast({ title: 'Error', description: 'Delete failed', variant: 'destructive' });
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>Manage Books ({books.length})</CardTitle>
          <div className="flex gap-2">
            <Button onClick={onExport}><Download className="w-4 h-4 mr-2" />Export CSV</Button>
            <Button onClick={handleAdd}><Plus className="w-4 h-4 mr-2" />Add Book</Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Author</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {books.map((book) => (
              <TableRow key={book.id}>
                <TableCell>{book.title}</TableCell>
                <TableCell>{book.author}</TableCell>
                <TableCell>{book.category}</TableCell>
                <TableCell>₦{book.price?.toLocaleString()}</TableCell>
                <TableCell><span className={`px-2 py-1 rounded text-xs ${book.status === 'available' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>{book.status}</span></TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleEdit(book)}><Edit className="w-4 h-4" /></Button>
                    <Button variant="destructive" size="sm" onClick={() => handleDelete(book.id)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingItem ? 'Edit Book' : 'Add Book'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div><Label>Title *</Label><Input required value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} /></div>
              <div><Label>Author *</Label><Input required value={formData.author} onChange={(e) => setFormData({ ...formData, author: e.target.value })} /></div>
              <div><Label>Description *</Label><Textarea required rows={4} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Category *</Label><Input required value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} /></div>
                <div><Label>Price (₦) *</Label><Input required type="number" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} /></div>
              </div>
              <div><Label>Cover Image URL</Label><Input value={formData.cover_image} onChange={(e) => setFormData({ ...formData, cover_image: e.target.value })} placeholder="https://..." /></div>
              <div><Label>PDF Filename</Label><Input value={formData.pdf_filename} onChange={(e) => setFormData({ ...formData, pdf_filename: e.target.value })} placeholder="book.pdf" /></div>
              <div><Label>Status *</Label><select required className="w-full border rounded px-3 py-2" value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })}><option value="available">Available</option><option value="out_of_stock">Out of Stock</option></select></div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit">{editingItem ? 'Update' : 'Create'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};